import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Phone, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fallbackCode, setFallbackCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const sendOTP = async () => {
    if (!phone || phone.length < 10) { setError('Please enter a valid phone number'); return; }
    setLoading(true);
    setError('');
    setFallbackCode('');
    try {
      const formatted = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
      const res = await axios.post(`${API}/auth/send-otp`, { phone: formatted });
      setPhone(formatted);
      // If SMS failed, backend returns the code as fallback
      if (res.data.code) {
        setFallbackCode(res.data.code);
      }
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!code || code.length < 6) { setError('Please enter the 6-digit code'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { phone, code });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const testLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/test-login`);
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (e) {
      setError('Test login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-6 relative">
      {/* Owner Login - Top Right */}
      <button
        data-testid="owner-login-link"
        onClick={() => navigate('/admin/login')}
        className="absolute top-6 right-6 text-text-muted/60 text-xs font-medium hover:text-espresso transition-colors tracking-wide uppercase"
      >
        Owner Login
      </button>

      <div className="w-full max-w-sm">
        <div className="text-center mb-12 animate-fade-in">
          <h1 data-testid="login-title" className="font-heading text-4xl tracking-tight text-espresso mb-3">
            HostBase
          </h1>
          <p className="text-text-muted text-base">Your personal property concierge</p>
        </div>

        {/* Test Bypass Button */}
        <button
          data-testid="test-login-btn"
          onClick={testLogin}
          disabled={loading}
          className="w-full mb-8 py-3 border border-dashed border-warmgold/50 rounded-full text-sm text-warmgold font-medium hover:bg-warmgold/5 transition-colors"
        >
          {loading ? 'Logging in...' : 'Test Login (+1 302-468-6277)'}
        </button>

        {step === 1 ? (
          <div className="space-y-6 animate-fade-in">
            <div>
              <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-3 block">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-0 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
                <Input
                  data-testid="phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="bg-transparent border-0 border-b border-warm-border pl-8 pr-0 py-3 text-lg rounded-none focus-visible:ring-0 focus-visible:border-espresso placeholder:text-text-muted/40 h-auto"
                  onKeyDown={(e) => e.key === 'Enter' && sendOTP()}
                />
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-warm-error text-sm">{error}</p>}
            <Button
              data-testid="send-otp-btn"
              onClick={sendOTP}
              disabled={loading}
              className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-13 text-base font-medium transition-transform active:scale-95"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Continue
              {!loading && <ArrowRight className="ml-2" size={18} />}
            </Button>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <div>
              <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-3 block">
                Verification Code
              </label>
              <p className="text-text-muted text-sm mb-4">
                {fallbackCode
                  ? 'SMS could not be delivered. Use the code below.'
                  : `We sent a 6-digit code to ${phone}`}
              </p>
              {fallbackCode && (
                <div data-testid="fallback-code" className="bg-sand border border-warm-border rounded-xl p-4 mb-4 text-center">
                  <p className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1">Your Code</p>
                  <p className="text-3xl font-bold text-espresso tracking-[0.3em] font-mono">{fallbackCode}</p>
                </div>
              )}
              <Input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="bg-transparent border-0 border-b border-warm-border px-0 py-3 text-3xl text-center tracking-[0.5em] rounded-none focus-visible:ring-0 focus-visible:border-espresso placeholder:text-text-muted/30 h-auto font-body"
                onKeyDown={(e) => e.key === 'Enter' && verifyOTP()}
              />
            </div>
            {error && <p data-testid="verify-error" className="text-warm-error text-sm">{error}</p>}
            <Button
              data-testid="verify-otp-btn"
              onClick={verifyOTP}
              disabled={loading}
              className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-13 text-base font-medium transition-transform active:scale-95"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Verify & Enter
            </Button>
            <button
              data-testid="resend-otp-btn"
              onClick={() => { setStep(1); setCode(''); setError(''); }}
              className="w-full text-center text-text-muted text-sm hover:text-espresso transition-colors"
            >
              Change phone number
            </button>
          </div>
        )}
      </div>

      {/* SMS Consent Notice */}
      <p data-testid="sms-consent-notice" className="max-w-sm text-center text-[11px] leading-relaxed text-text-muted/70 mt-10 px-4">
        By entering your phone number and tapping Continue, you consent to receive a one-time verification code via SMS. Message and data rates may apply. Your phone number will only be used for authentication and order updates. View our{' '}
        <span className="underline underline-offset-2 cursor-pointer" onClick={() => navigate('/privacy')}>Privacy Policy</span> and{' '}
        <span className="underline underline-offset-2 cursor-pointer" onClick={() => navigate('/terms')}>Terms of Service</span>.
      </p>
    </div>
  );
}
